<template>
    <div class="card" v-if="skeleton">
        <div class="card-body d-flex flex-column p-9">
            <el-skeleton />
        </div>
    </div>
    <div v-else>
        <div class="card mb-7">
            <div class="card-header border-0 pt-6">
                <div class="card-title flex-column">
                    <h3 class="fw-bolder m-0">{{ $t(typeKey + '.title') }}</h3>
                    <span class="text-gray-400 fw-bold fs-7 mt-1">{{ $t(typeKey + '.subTitle') }}</span>
                </div>
            </div>
            <div class="card-body pt-2">
                <label class="fs-6 fw-bold mb-3">{{ $t('pages.ecommerce.productManagement.product.relatedProductSettings.mode') }}</label>
                <div class="row g-5">
                    <div :class="modes.length > 2 ? 'col-md-4' : 'col-md-6'" v-for="mode in modes" :key="mode">
                        <label class="btn btn-outline btn-outline-dashed d-flex text-start p-6 h-100" :class="{'btn-outline-primary btn-active-light-primary active': currentMode === mode, 'btn-outline-default': currentMode !== mode}">
                            <input type="radio" class="form-check-input me-4 mt-1" :value="mode" :checked="currentMode === mode" :disabled="modeLoading" @change="changeMode(mode)">
                            <span class="d-block">
                                <span class="text-dark fw-bolder d-block fs-5 mb-1">{{ $t(typeKey + '.modes.' + mode + '.title') }}</span>
                                <span class="text-gray-600 fw-bold fs-7">{{ $t(typeKey + '.modes.' + mode + '.description') }}</span>
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="card mb-7" v-if="currentMode === 'MANUAL'">
            <div class="card-header border-0 pt-6">
                <div class="card-title flex-column">
                    <h3 class="fw-bolder m-0">{{ $t('pages.ecommerce.productManagement.product.relatedProductSettings.manualTitle') }}</h3>
                    <span class="text-gray-400 fw-bold fs-7 mt-1">{{ sprintf($t(typeKey + '.manualSubTitle'), [limit]) }}</span>
                </div>
            </div>
            <div class="card-body pt-2">
                <div class="d-flex mb-7">
                    <el-select v-model="form.related_product_ids" class="flex-grow-1 me-3" multiple filterable remote reserve-keyword :remote-method="queueSearch" :loading="search.loading" :no-data-text="searchHint" :placeholder="$t('pages.ecommerce.productManagement.product.relatedProductSettings.chooseProducts')">
                        <el-option v-for="product in selectOptions" :key="product.id" :value="product.id" :label="product.title + ' #' + product.id">
                            <span>{{ product.title }} <span class="text-gray-500">({{ product.group_name }}) #{{ product.id }}</span></span>
                            <span v-if="!product.active" class="badge badge-light-danger ms-2">{{ $t('pages.ecommerce.productManagement.product.relatedProductSettings.passive') }}</span>
                        </el-option>
                    </el-select>
                    <button class="btn btn-primary" type="button" :data-kt-indicator="form.loading ? 'on' : null" :disabled="form.loading || !form.related_product_ids.length" @click="addProducts">
                        <span v-if="!form.loading" class="indicator-label">{{ $t('btn.add') }}</span>
                        <span v-else class="indicator-progress">{{ $t('messages.wait') }} <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
                    </button>
                </div>

                <div class="table-responsive">
                    <table class="table align-middle table-row-dashed fs-6 gy-4 mb-0">
                        <thead>
                            <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                                <th class="w-125px">{{ $t('pages.ecommerce.productManagement.product.relatedProductSettings.cols.sort') }}</th>
                                <th>{{ $t('pages.ecommerce.productManagement.product.cols.title') }}</th>
                                <th>{{ $t('pages.ecommerce.productManagement.product.cols.group') }}</th>
                                <th class="text-end">{{ $t('common.action') }}</th>
                            </tr>
                        </thead>
                        <tbody class="fw-bold text-gray-600">
                            <tr v-if="!items.length">
                                <td colspan="4" class="text-center text-gray-500 py-10">{{ $t('pages.ecommerce.productManagement.product.relatedProductSettings.emptyManual') }}</td>
                            </tr>
                            <tr v-for="record in items" :key="record.id">
                                <td>
                                    <el-input-number v-model="record.sort" size="small" :min="-128" :max="127" controls-position="right" class="w-100px" @change="queueSortUpdate(record)"/>
                                </td>
                                <td>
                                    <a class="fw-bold text-gray-600 text-hover-primary" :href="$root.appUrl + '/' + record.translate.slug" target="_blank">{{ record.translate.title }}</a>
                                    <span v-if="!record.related_product.active || record.related_product.deleted_at" class="badge badge-light-danger ms-2">{{ $t('pages.ecommerce.productManagement.product.relatedProductSettings.notVisible') }}</span>
                                </td>
                                <td>{{ record.related_product.group ? record.related_product.group.name : '-' }}</td>
                                <td class="text-end">
                                    <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord(record.id)">
                                        <template #reference>
                                            <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                                <span class="svg-icon svg-icon-3">
                                                    <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                                                </span>
                                            </a>
                                        </template>
                                    </el-popconfirm>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="card" v-if="currentMode !== 'DISABLED'">
            <div class="card-header border-0 pt-6">
                <div class="card-title flex-column">
                    <h3 class="fw-bolder m-0">{{ $t(typeKey + '.previewTitle') }}</h3>
                    <span class="text-gray-400 fw-bold fs-7 mt-1">{{ $t(typeKey + '.previewSubTitle') }}</span>
                </div>
            </div>
            <div class="card-body pt-2" v-loading="loading">
                <div v-if="!preview.length" class="text-center text-gray-500 py-5">{{ $t(typeKey + '.emptyPreview') }}</div>
                <div v-else class="d-flex flex-wrap gap-3">
                    <a v-for="(product, index) in preview" :key="product.id" :href="$root.appUrl + '/' + product.translate.slug" target="_blank" class="badge badge-light-primary fs-7 fw-bold py-3 px-4">
                        {{ index + 1 }}. {{ product.translate.title }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        productID: {
            required: true
        },
        type: {
            type: String,
            default: 'RELATED'
        }
    },
    name: "RelatedProduct",
    data() {
        return {
            skeleton: true,
            loading: false,
            modeLoading: false,
            currentMode: 'DISABLED',
            modes: [],
            limit: 8,
            items: [],
            preview: [],
            sortTimers: {},
            search: {
                loading: false,
                query: '',
                timer: null,
                results: [],
                cache: {}
            },
            form: {
                loading: false,
                related_product_ids: []
            }
        }
    },
    computed: {
        typeKey() {
            return 'pages.ecommerce.productManagement.product.relatedProductSettings.types.' + this.type;
        },
        searchHint() {
            return this.search.query.trim().length < 2
                ? this.$t('pages.ecommerce.productManagement.product.relatedProductSettings.searchMinChar')
                : this.$t('pages.ecommerce.productManagement.product.relatedProductSettings.searchNoResult');
        },
        selectOptions() {
            let options = this.form.related_product_ids.map((id) => this.search.cache[id]).filter(Boolean);
            let selectedIDs = this.form.related_product_ids;

            return options.concat(this.search.results.filter((product) => !selectedIDs.includes(product.id)));
        }
    },
    mounted() {
        this.loadRelatedProducts().finally(() => {
            this.skeleton = false;
        });
    },
    methods: {
        queueSearch(query) {
            this.search.query = query || '';
            clearTimeout(this.search.timer);

            if (this.search.query.trim().length < 2) {
                this.search.results = [];
                this.search.loading = false;
                return;
            }

            this.search.loading = true;
            this.search.timer = setTimeout(() => {
                this.searchProducts(this.search.query.trim());
            }, 300);
        },
        searchProducts(query) {
            this.axios.get(this.endpoints['ecommerce_product_related_product'] + '/search', {
                params: { product_id: this.productID, type: this.type, q: query }
            }).then((response) => {
                if (query !== this.search.query.trim()) {
                    return;
                }

                this.search.results = response.data.data;
                this.search.results.forEach((product) => {
                    this.search.cache[product.id] = product;
                });
            }).finally(() => {
                if (query === this.search.query.trim()) {
                    this.search.loading = false;
                }
            });
        },
        loadRelatedProducts() {
            this.loading = true;

            return this.axios.get(this.endpoints['ecommerce_product_related_product'], {
                params: { product_id: this.productID, type: this.type }
            }).then((response) => {
                let data = response.data.data;

                this.currentMode = data.mode;
                this.modes = data.modes;
                this.limit = data.limit;
                this.items = data.items.map((item) => {
                    item.translate = this.resolveDatum(item.related_product.translations, this.$root.defaultLanguage.id, 'language_id');

                    return item;
                });
                this.preview = data.preview.map((product) => {
                    product.translate = this.resolveDatum(product.translations, this.$root.defaultLanguage.id, 'language_id');

                    return product;
                });
            }).finally(() => {
                this.loading = false;
            });
        },
        changeMode(mode) {
            this.modeLoading = true;

            this.axios.post(this.endpoints['ecommerce_product_related_product'] + '/mode', {
                product_id: this.productID,
                type: this.type,
                mode: mode
            }).then((response) => {
                this.onResponse(response.data, () => {
                    this.currentMode = mode;
                    this.loadRelatedProducts();
                    this.$emit('loadProduct', this.productID);
                });
            }).catch((error) => {
                this.onResponseFailure(error.response.data);
            }).finally(() => {
                this.modeLoading = false;
            });
        },
        addProducts() {
            this.form.loading = true;

            this.axios.post(this.endpoints['ecommerce_product_related_product'], {
                product_id: this.productID,
                type: this.type,
                related_product_ids: this.form.related_product_ids
            }).then((response) => {
                this.onResponse(response.data, () => {
                    this.form.related_product_ids = [];
                    this.search.results = [];
                    this.search.query = '';
                    this.loadRelatedProducts();
                });
            }).catch((error) => {
                this.onResponseFailure(error.response.data);
            }).finally(() => {
                this.form.loading = false;
            });
        },
        queueSortUpdate(record) {
            clearTimeout(this.sortTimers[record.id]);

            this.sortTimers[record.id] = setTimeout(() => {
                delete this.sortTimers[record.id];
                this.updateSort(record);
            }, 700);
        },
        updateSort(record) {
            this.axios.put(this.endpoints['ecommerce_product_related_product'] + '/' + record.id, {
                sort: record.sort
            }).then((response) => {
                this.onResponse(response.data, () => {
                    if (!Object.keys(this.sortTimers).length) {
                        this.loadRelatedProducts();
                    }
                });
            }).catch((error) => {
                this.onResponseFailure(error.response.data);
            });
        },
        deleteRecord(id) {
            this.axios.delete(this.endpoints['ecommerce_product_related_product'] + '/' + id).then((response) => {
                this.onResponse(response.data, () => {
                    this.loadRelatedProducts();
                });
            }).catch((error) => {
                this.onResponseFailure(error.response.data);
            });
        }
    }
}
</script>
